/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ICartLinePriceEditorResources } from '@msdyn365-commerce/components';
import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ChannelDeliveryOptionConfiguration, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { CartLine, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, Modal, INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import { AiFillFileWord, AiFillFileExcel, AiFillFilePpt, AiFillFilePdf, AiOutlineHtml5, AiFillFile, AiFillFileImage } from 'react-icons/ai';
import * as React from 'react';
// import { getProductDetails } from './../../../../utilities/get-product-details';
import { getArtifactsByItemAsync } from './../../../../actions/extensions/DataActionExtension.g';
import { AddToCartComponent } from './addtocart.component';
import {
    FaLeanpub,
    FaGoogleDrive,
    FaFileWord,
    FaFileImage,
    FaFileCsv,
    FaFileCode,
    FaFileVideo,
    FaFileInvoice,
    FaFileSignature,
    FaFileArchive,
    FaFileExcel,
    FaFilePowerpoint,
    FaHtml5,
    FaFileAudio
} from 'react-icons/fa';
import {
    BsFillFileEarmarkBarGraphFill,
    BsFileEarmarkRichtextFill,
    BsFileEarmarkPostFill,
    BsFileEarmarkFontFill,
    BsFileEarmarkCodeFill,
    BsFileEarmarkEaselFill
} from 'react-icons/bs';
import { RiFileGifFill } from 'react-icons/ri';
import { SiMicrosoftoutlook } from 'react-icons/si';
import { KWR, ACTION_BUTTONS, ADD_TO_KETE_SITE_LANGUAGE } from '../../../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage } from '../../../../utilities/language-change';
import { HiDocumentReport } from 'react-icons/hi';

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */
export interface ICartlineResourceString {
    /**
     * Display string for discount label
     */
    discountStringText: string;

    /**
     * String for size key
     */
    sizeString: string;

    /**
     * String for color key
     */
    colorString: string;

    /**
     * String associated with the configuration product dimension
     */
    configString: string;

    /**
     * String for style key
     */
    styleString: string;

    /**
     * String for amount key
     */
    amountString?: string;

    /**
     * Display string for quantity label
     */
    quantityDisplayString: string;

    /**
     * Display string for quantity label
     */
    inputQuantityAriaLabel: string;

    /**
     * Aria label for the decrement button in quantity component
     */
    decrementButtonAriaLabel?: string;

    /**
     * Aria label for the increment button in quantity component
     */
    incrementButtonAriaLabel?: string;

    /**
     * Original text screenreader
     */
    originalPriceText: string;

    /**
     * Current text screenreader
     */
    currentPriceText: string;

    /**
     * Shipping Charges Text
     */
    shippingChargesText: string;

    priceEditorResources?: ICartLinePriceEditorResources;
}

interface ICartLineData {
    cartLine: CartLine | SalesLine;
    cartState?: ICartState;
    product?: SimpleProduct;
}

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /**
     * The flag to change the quantity component from intractable to static.
     */
    disableQuantityInteractivity?: boolean;

    /**
     * The primary image url.
     */
    primaryImageUrl?: string;

    /**
     * The product url.
     */
    productUrl?: string;

    /**
     * GridSettings for the product image in cartLine.
     */
    gridSettings: IGridSettings;

    /**
     * ImageSettings for the product image in cartLine.
     */
    imageSettings: IImageSettings;

    /**
     * Boolean flag to indicate if the item is out of stock.
     */
    isOutOfStock?: boolean;

    /**
     * Flag to make quantity section editable.
     */
    isQuantityEditable?: boolean;

    /**
     * Max quantity for line item.
     */
    maxQuantity?: number;

    /**
     * Current quantity for line item.
     */
    currentQuantity?: number;

    /**
     * Resource string for the component.
     */
    resources: ICartlineResourceString;

    /**
     * SalesLine flag.
     */
    isSalesLine?: boolean;

    /**
     * Error message to show in place of quantity.
     */
    errorMessage?: string;

    /**
     * Inventory information label.
     */
    inventoryInformationLabel?: string;

    /**
     * Inventory information class name.
     */
    inventoryLabelClassName?: string;

    /**
     * Flag to show/hide shipping charges for line items.
     */
    showShippingChargesForLineItems?: boolean;

    /**
     * Boolean flag to indicate if cart state status is ready.
     */
    isCartStateReady?: boolean;

    /**
     * Chanel Delivery Option configuration is from api.
     */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;

    /**
     * The cart line index.
     */
    lineIndex?: number;

    /**
     * Quantity onChange callback.
     */
    quantityOnChange?(cartLine: CartLine, newQuantity: number, lineIndex?: number): boolean;
    // quickViewButton?: React.ReactNode;
}

const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { productUrl, context } = props;
    const { product, cartLine } = props.data;
    // const editProductText: string = 'Edit';
    // const DownloadCatridgeText: string = 'Download as common catridge ?';
    const [artifacts, setArtifacts] = React.useState([]);
    const [previouslySelectedArtifacts, setpreviouslySelectedArtifacts] = React.useState<string[]>([]);
    const [htmlResource, setHtmlResource] = React.useState('');
    const [allArtifacts, setAllArtifacts] = React.useState<string[]>([]);
    const [selectedArtifacts, setSelectedArtifacts] = React.useState<string[]>([]);
    const [resourcePageURL, setResourcePageURL] = React.useState<string>('');
    const [previouslySelectedResourcePageURL, setpreviouslySelectedResourcePageURL] = React.useState<string>('');

    // Identifying Language
    const toggleLang = getToggleLang(context);
    const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
    const preferredLanguage: any = getUserPreferredLanguage(KWR, ACTION_BUTTONS, userPrefLang, toggleLang, context);
    const preferredKeteSiteLanguage: any = getUserPreferredLanguage(KWR, ADD_TO_KETE_SITE_LANGUAGE, userPrefLang, toggleLang, context);

    // Language Variables
    const languageResources = {
        finaliseKeteText: 'Finalise your kete to download this resource',
        finaliseKeteTextInMaori: 'Whakaotia tō kete hei tikiake i tēnei rauemi',
        selectAllText: 'Select all',
        selectAllTextInMaori: 'Tīpakohia te katoa',
        selectResourcesText: 'Please select the materials you would like to download:',
        selectResourcesTextInMaori: 'Tohua mai ngā rauemi hei tikiake māu:',
        editButtonText: 'Edit',
        editButtonTextInMaori: 'Whakatika',
        updateKeteButtonText: preferredLanguage === 'maori' ? 'Whakahoutia te kāhui' : 'Update Kete'
    };

    console.log('Cart Line props', props);

    React.useEffect(() => {
        let artifactString: any =
            cartLine && cartLine?.ExtensionProperties && cartLine?.ExtensionProperties.length > 0
                ? cartLine.ExtensionProperties[0]?.Value?.StringValue
                : '';
        let htmlResourceURL: any =
            cartLine && cartLine?.ExtensionProperties && cartLine?.ExtensionProperties.length > 1
                ? cartLine.ExtensionProperties[1]?.Value?.StringValue
                : '';
        let productURL: any =
            cartLine && cartLine?.ExtensionProperties && cartLine?.ExtensionProperties.length > 2
                ? cartLine.ExtensionProperties[2]?.Value?.StringValue
                : '';
        let artifacts = artifactString ? JSON.parse(artifactString) : null;
        if (artifacts) {
            let selectedArtifactIds = artifacts.map(function(el: any) {
                return el.ArtifactId;
            });
            setpreviouslySelectedArtifacts(selectedArtifactIds);
            setpreviouslySelectedResourcePageURL(htmlResourceURL);
            setSelectedArtifacts(selectedArtifactIds);
            setArtifacts(artifacts);
            setHtmlResource(htmlResourceURL);
            setResourcePageURL(productURL);
        }
    }, [cartLine]);

    React.useEffect(() => {
        getArtifacts()?.then((artifacts: any) => {
            setAllArtifacts(artifacts);
        });
        // if (props.productDetails?.product) {
        //     setProduct(props.productDetails.product);
        // }
    }, [product]);

    function getArtifacts() {
        try {
            const context: any = { callerContext: props.context.actionContext };
            console.log('Get Props inside get Artifacts');
            // return getArtifactsByItemAsync(context, 'NZH30').then(result => result);
            return getArtifactsByItemAsync(context, product?.ItemId as string).then((result: any) => {
                return result.filter((item: any) => {
                    return (
                        item?.FileType?.toUpperCase() !== 'IMSCC' &&
                        item?.FileType?.toUpperCase() !== 'WEBPAGE' &&
                        !(item?.MediaName as string).includes('CogSearchIndexHelper') &&
                        !item?.IsRetire
                    );
                });
            });
        } catch (err) {
            console.log(err);
            return;
        }
    }
    // const destructDimensionStrings = {
    //     sizeString: resources.sizeString,
    //     colorString: resources.colorString,
    //     styleString: resources.styleString,
    //     configString: resources.configString,
    //     amountString: resources.amountString
    // };

    const fallbackImageUrl = product && getFallbackImageUrl(product.ItemId, props.context.actionContext.requestContext.apiSettings);
    // const productDimensions = product && CartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const imageSettings = props.imageSettings;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    // const renderDisountLines = CartLineItemFunctions.renderDiscountLines(props);
    // const renderInventoryLabel = CartLineItemFunctions.renderInventoryLabel(props);
    // const renderUnitOfMeasure = CartLineItemFunctions.renderUnitOfMeasure(props);
    // const renderShippingLabel = CartLineItemFunctions.renderShippingLabel(props);
    // const renderOtherCharges = CartLineItemFunctions.renderOtherCharges(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const productAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Name || cartLine.Description;
    const imgeClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-image' : 'msc-cart-line__product-image';
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const _generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-cart-line__error-message msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    function fnOnEdit() {
        setIsModalOpen(true);
        // var quickviewDiv = document.querySelector('.ms-cart-icon__minicartmodal-container');
        // var closeButton = quickviewDiv?.querySelector('.ms-cart-icon__btn-close') as HTMLElement;
        // closeButton.click();
    }

    function renderDocumentIcon(itemIconType: any) {
        if (itemIconType === 'DOC') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiFillFileWord size={50}></AiFillFileWord>
                </i>
            );
        } else if (itemIconType === 'IMAGE') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiFillFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'EXCEL') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiFillFileExcel size={50}></AiFillFileExcel>
                </i>
            );
        } else if (itemIconType === 'PPT') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiFillFilePpt size={50}></AiFillFilePpt>
                </i>
            );
        } else if (itemIconType === 'EPUB') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaLeanpub size={50}></FaLeanpub>
                </i>
            );
        } else if (itemIconType === 'PDF') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiFillFilePdf size={50}></AiFillFilePdf>
                </i>
            );
        } else if (itemIconType === 'GOOGLEDOC') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaGoogleDrive size={50} />
                </i>
            );
        } else if (itemIconType === 'HTML') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiOutlineHtml5 size={50} />
                </i>
            );
        } else if (itemIconType === 'AVI') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileVideo size={50} />
                </i>
            );
        } else if (itemIconType === 'BMP') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'CSV') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileCsv size={50} />
                </i>
            );
        } else if (itemIconType === 'DOCX') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileWord size={50} />
                </i>
            );
        } else if (itemIconType === 'GIF') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <RiFileGifFill size={50} />
                </i>
            );
        } else if (itemIconType === 'HTM') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaHtml5 size={50} />
                </i>
            );
        } else if (itemIconType === 'JPEG') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'JPG') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'JSON') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileInvoice size={50} />
                </i>
            );
        } else if (itemIconType === 'MHP') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFillFileEarmarkBarGraphFill size={50} />
                </i>
            );
        } else if (itemIconType === 'MHTML') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileCode size={50} />
                </i>
            );
        } else if (itemIconType === 'MSG') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <SiMicrosoftoutlook size={50} />
                </i>
            );
        } else if (itemIconType === 'PNG') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'PPT') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFilePowerpoint size={50} />
                </i>
            );
        } else if (itemIconType === 'PPTX') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFilePowerpoint size={50} />
                </i>
            );
        } else if (itemIconType === 'RTF') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFileEarmarkRichtextFill size={50} />
                </i>
            );
        } else if (itemIconType === 'SGML') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFileEarmarkPostFill size={50} />
                </i>
            );
        } else if (itemIconType === 'TIF') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'TXT') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFileEarmarkFontFill size={50} />
                </i>
            );
        } else if (itemIconType === 'VSD') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileSignature size={50} />
                </i>
            );
        } else if (itemIconType === 'WMF') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileImage size={50} />
                </i>
            );
        } else if (itemIconType === 'XHTML') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileCode size={50} />
                </i>
            );
        } else if (itemIconType === 'XLS') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileExcel size={50} />
                </i>
            );
        } else if (itemIconType === 'XLSX') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileExcel size={50} />
                </i>
            );
        } else if (itemIconType === 'XML') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFileEarmarkCodeFill size={50} />
                </i>
            );
        } else if (itemIconType === 'ZIP') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileArchive size={50} />
                </i>
            );
        } else if (itemIconType === 'GSLIDES') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFilePowerpoint size={50} />
                </i>
            );
        } else if (itemIconType === 'WAV') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileAudio size={50} />
                </i>
            );
        } else if (itemIconType === 'MP3') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileAudio size={50} />
                </i>
            );
        } else if (itemIconType === 'WMA') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileAudio size={50} />
                </i>
            );
        } else if (itemIconType === 'MP4') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileVideo size={50} />
                </i>
            );
        } else if (itemIconType === 'MPEG') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileVideo size={50} />
                </i>
            );
        } else if (itemIconType === 'MOV') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileVideo size={50} />
                </i>
            );
        } else if (itemIconType === 'PIBX') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <HiDocumentReport size={50} />
                </i>
            );
        } else if (itemIconType === 'USDZ') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFileEarmarkEaselFill size={50} />
                </i>
            );
        } else if (itemIconType === 'DAE') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <BsFileEarmarkEaselFill size={50} />
                </i>
            );
        } else if (itemIconType === 'XSN') {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <FaFileCode size={50} />
                </i>
            );
        } else {
            return (
                <i className='msc-cart-line__product-resource__img'>
                    <AiFillFile size={50} />
                </i>
            );
        }
    }

    const _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-quickView__dialog',
            wrapClassName: 'ms-quickView__wrap',
            isOpen: isModalOpen,
            toggle: closeModal
        };
    };

    const closeModal = (): void => {
        setIsModalOpen(false);
    };
    function renderWordFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiFillFileWord></AiFillFileWord> <span>{fileName}</span>
                <input checked={checked} onChange={e => fileCheckboxOnChange(e, artifactId)} type='checkbox' aria-label={fileName} />
            </p>
        );
    }

    function renderExcelFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiFillFileExcel></AiFillFileExcel> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderPPTFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiFillFilePpt></AiFillFilePpt> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderPDFFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiFillFilePdf></AiFillFilePdf> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderEPUBFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaLeanpub></FaLeanpub> <span>{fileName}</span>
                <input type='checkbox' aria-label={fileName} checked={checked} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderImageFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiFillFileImage /> <span>{fileName}</span>
                <input type='checkbox' aria-label={fileName} checked={checked} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderGoogleDocFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaGoogleDrive /> <span>{fileName}</span>
                <input type='checkbox' aria-label={fileName} checked={checked} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderHTMLFile() {
        let checked = htmlResource ? true : false;
        return (
            <p>
                <AiOutlineHtml5 /> <span>{productName}</span>
                <input type='checkbox' aria-label='HTML File' checked={checked} onChange={e => fileCheckboxOnChange(e, '0')} />
            </p>
        );
    }

    function renderHTMLFileArtifact(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiOutlineHtml5 size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderAviFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileVideo size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderBmpFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileImage size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderCsvFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileCsv size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderDocFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileWord size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderGifFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <RiFileGifFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderJSONFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileInvoice size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderMhpFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFillFileEarmarkBarGraphFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderCodeFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileCode size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderMsgFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <SiMicrosoftoutlook size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderRTFFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFileEarmarkRichtextFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderSGMLFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFileEarmarkPostFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderTxtFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFileEarmarkFontFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderVsdFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileSignature size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderXmlFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFileEarmarkCodeFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderZipFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileArchive size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderAudioFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileAudio size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderVideoFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileVideo size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderPbixFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <HiDocumentReport size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderUsdzFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFileEarmarkEaselFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderDaeFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <BsFileEarmarkEaselFill size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderXsnFile(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <FaFileCode size={50} /> <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderotherFiles(fileURL: any, fileName: any, artifactId: any, checked: boolean) {
        return (
            <p>
                <AiFillFile />
                <span>{fileName}</span>
                <input checked={checked} type='checkbox' aria-label={fileName} onChange={e => fileCheckboxOnChange(e, artifactId)} />
            </p>
        );
    }

    function renderDocument(item: any) {
        let checked = selectedArtifacts.some(x => x === item.ArtifactId);

        if (item?.FileType?.toUpperCase() === 'DOC') {
            return renderWordFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'IMAGE') {
            return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'EXCEL') {
            return renderExcelFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'PPT') {
            return renderPPTFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'EPUB') {
            return renderEPUBFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'PDF') {
            return renderPDFFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'GOOGLEDOC') {
            return renderGoogleDocFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'HTML') {
            return renderHTMLFileArtifact(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'AVI') {
            return renderAviFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'BMP') {
            return renderBmpFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'CSV') {
            return renderCsvFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'DOC') {
            return renderDocFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'DOCX') {
            return renderDocFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'GIF') {
            return renderGifFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'HTM') {
            return renderHTMLFileArtifact(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'JPEG') {
            return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'JPG') {
            return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'JSON') {
            return renderJSONFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MHP') {
            return renderMhpFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MHTML') {
            return renderCodeFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MSG') {
            return renderMsgFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'PNG') {
            return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'PPT') {
            return renderPPTFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'PPTX') {
            return renderPPTFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'RTF') {
            return renderRTFFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'SGML') {
            return renderSGMLFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'TIF') {
            return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'TXT') {
            return renderTxtFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'VSD') {
            return renderVsdFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'WMF') {
            return renderImageFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'XHTML') {
            return renderCodeFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'XLS') {
            return renderExcelFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'XLSX') {
            return renderExcelFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'XML') {
            return renderXmlFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'ZIP') {
            return renderZipFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'WAV') {
            return renderAudioFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MP3') {
            return renderAudioFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'WMA') {
            return renderAudioFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MP4') {
            return renderVideoFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MPEG') {
            return renderVideoFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'MOV') {
            return renderVideoFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'PIBX') {
            return renderPbixFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'USDZ') {
            return renderUsdzFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'DAE') {
            return renderDaeFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else if (item?.FileType?.toUpperCase() === 'XSN') {
            return renderXsnFile(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        } else {
            return renderotherFiles(item.MediaUrl, item.MediaName, item.ArtifactId, checked);
        }
    }

    function fileCheckboxOnChange(event: React.ChangeEvent<HTMLInputElement>, artifactId: string) {
        console.log(artifactId);
        if (artifactId != '0') {
            let selectedArtifactIds = selectedArtifacts;

            if (event?.target?.checked) {
                selectedArtifactIds = [...selectedArtifactIds, artifactId];
            } else {
                selectedArtifactIds = selectedArtifacts.filter(el => el !== artifactId);
            }
            console.log('selectedArtifactIds: ', selectedArtifactIds);
            setSelectedArtifacts(selectedArtifactIds);
        } else {
            console.log(htmlResource);
            if (event?.target?.checked) {
                setHtmlResource(resourcePageURL);
            } else {
                setHtmlResource('');
            }
        }
    }

    function fnAreAllSelected():boolean {
        let returnValue = false;
        if ((selectedArtifacts.length === allArtifacts.length) && (htmlResource !== '')) {returnValue = true;}
        return returnValue;
    }

    function fnOnSelectallClick() {
        let selectedArtifactIds = allArtifacts.map(function(item: any) {
            return item.ArtifactId;
        });
        setSelectedArtifacts(selectedArtifactIds);
        setHtmlResource(resourcePageURL);
    }

    function fnOnDeselectallClick() {
        selectedArtifacts.splice(0, selectedArtifacts.length);
        setHtmlResource('');
    }

    function fnHaveSelectedChanged():boolean {
        //compare previouslySelectedArtifacts and selectedArtifacts
        if (previouslySelectedArtifacts.length === 0 && selectedArtifacts.length === 0 && previouslySelectedResourcePageURL === htmlResource) {
            return false;
        }
        if (previouslySelectedArtifacts.length !== selectedArtifacts.length || previouslySelectedResourcePageURL !== htmlResource) {
            return true;
        } else {
            let tempValue = true;

            //test all elements
            previouslySelectedArtifacts.sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: "base" })
            ).forEach((element, index) => {
                tempValue = tempValue && element === selectedArtifacts[index]
            })

            //test the end item, as it's not in the array
            tempValue = tempValue && previouslySelectedResourcePageURL === htmlResource

            // if true then all test show no change, so invert value to return
            return !tempValue;
        }
    }

    return (
        <>
            <div className='msc-cart-line'>
                <div className={imgeClassName}>
                    <Image
                        requestContext={props.context.actionContext.requestContext}
                        src={props.primaryImageUrl ?? 'empty'}
                        fallBackSrc={fallbackImageUrl}
                        altText={productName}
                        gridSettings={props.gridSettings}
                        imageSettings={imageSettings}
                        loadFailureBehavior='empty'
                    />
                </div>
                <div className='msc-cart-line__content'>
                    <div className='msc-cart-line__product'>
                        <div className='msc-cart-line__product-text'>
                            <a className='msc-cart-line__product-title' href={productUrl} {...productAttribute}>
                                {productName}
                            </a>
                            {/* <p>{product?.Description}</p> */}
                            <p>
                                {preferredKeteSiteLanguage === 'maori'
                                    ? languageResources.finaliseKeteTextInMaori
                                    : languageResources.finaliseKeteText}
                            </p>
                        </div>
                        <div className='msc-cart-line__product-resources'>
                            <div className='msc-cart-line__product-resource'>
                                {artifacts.map((item: any, index: number): any => {
                                    if (!(item?.MediaName as string).includes('CogSearchIndexHelper')) {
                                        return renderDocumentIcon(item?.FileType?.toUpperCase());
                                    }
                                })}
                                {htmlResource && renderDocumentIcon('HTML')}
                            </div>
                            <div className='msc-cart-line__product-edit'>
                                <button
                                    onClick={() => {
                                        fnOnEdit();
                                    }}
                                    className='moe-btn moe-btn--primary-blue'
                                >
                                    {preferredLanguage === 'maori'
                                        ? languageResources.editButtonTextInMaori
                                        : languageResources.editButtonText}
                                </button>

                                {/* {props.quickViewButton && renderQuickView(props.quickViewButton, product!.RecordId)} */}
                            </div>
                        </div>

                        {/* <div className='msc-cart-line__product-downloadcatridge'>
                            <p className='msc-cart-line__product-resource'>{DownloadCatridgeText}</p>
                            <div className='msc-cart-line__product-edit'>
                                <input type='checkbox' aria-label={fileName} />
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>

            <Node {..._modalContainer()}>
                <div className='msc-modal__header'>
                    <h5 className='msc-modal__title'>
                        <div className='msc-quickview__heading'>
                            <h1 className='ms-quickView__product-title'>{productName}</h1>
                        </div>
                    </h5>
                    <button
                        type='button'
                        onClick={() => {
                            setIsModalOpen(false);
                        }}
                        className='msc-modal__close-button'
                        aria-label='Close'
                    ></button>
                </div>
                <div className='ms-quickView__body msc-modal__body'>
                    <div className='ms-quickView__content'>
                        <p className='m-b-4 fw-bold'>
                            {preferredKeteSiteLanguage === 'maori'
                                ? languageResources.selectResourcesTextInMaori
                                : languageResources.selectResourcesText}
                        </p>
                        {allArtifacts && (
                            <>
                                <div className='text-left m-b-4 fw-bold'>
                                    <p>{
                                        preferredKeteSiteLanguage === 'maori'
                                            ? languageResources.selectAllTextInMaori
                                            : languageResources.selectAllText
                                        }
                                        <input type='checkbox' aria-label='HTML File' checked={fnAreAllSelected()} onChange={fnAreAllSelected() ? () => fnOnDeselectallClick() : () => fnOnSelectallClick()} />
                                    </p>
                                </div>
                                <ul className='moe-doclist'>
                                    {allArtifacts?.map((item: any, index: number): any => {
                                        // let indexKey: string;

                                        // if (item.image) {
                                        //     const image = (item.image as unknown) as Msdyn365.Image;
                                        //     indexKey = image.props.src as string;
                                        // } else {
                                        //     const text = (item.documentType as unknown) as Msdyn365.RichTextComponent;
                                        //     indexKey = text.props.text as string;
                                        // }
                                        if (!(item?.MediaName as string).includes('CogSearchIndexHelper')) {
                                            return (
                                                <li className='moe-doclist__item' key={index}>
                                                    {renderDocument(item)}
                                                </li>
                                            );
                                        }
                                    })}
                                    <li className='moe-doclist__item'>{renderHTMLFile()}</li>
                                </ul>
                            </>
                        )}
                    </div>
                </div>
                <div className='ms-quickView__footer msc-modal__footer'>
                    <div className='ms-quickView__product-add-to-cart'>
                        {product && (
                            <AddToCartComponent
                                addToCartText={languageResources.updateKeteButtonText}
                                context={props.context}
                                id={props.id}
                                typeName={props.typeName}
                                data={{ product: product }}
                                artifactsData={allArtifacts.filter(function(el: any) {
                                    return selectedArtifacts.indexOf(el.ArtifactId) >= 0;
                                })}
                                resourcePageURL={htmlResource}
                                productUrl={resourcePageURL}
                                isEdit={true}
                                cartLineID={cartLine.LineId}
                                disabled={!fnHaveSelectedChanged()}
                            ></AddToCartComponent>
                        )}
                    </div>
                </div>
            </Node>

            {_generateErrorMessage()}
        </>
    );
};

export default CartLine;
